import React, { useState } from 'react';
import './RecentArticlesCarousel.scss';
import { useSelector } from 'react-redux';
import { Card } from 'react-bootstrap';
import Carousel from 'react-simply-carousel';
import { Link } from 'react-router-dom';
import { createdMonthDayYear, htmlContentWithLink, stripMarkdown } from '../../../commonservice';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addPageAction } from '../../../Redux/slices/pageDetailSlice';
import { getPostComments } from '../../../Redux/slices/post';
import { RouteConstants } from '../../../Constants/routeConstants';

interface RecentArticlesCarouselProps {
  userData: any;
  pageData: any;
  canAction: () => boolean;
  setModalShow: (show: boolean) => void;
  setActivePost: (post: any) => void;
  stopAllMedia: () => void;
}

const RecentArticlesCarousel: React.FC<RecentArticlesCarouselProps> = ({
  userData,
  pageData,
  canAction,
  setModalShow,
  setActivePost,
  stopAllMedia,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeSlide, setActiveSlide] = useState(0);
  
  const posts = useSelector((state: any) => state?.post?.postList?.data || []);

  const recentArticles = React.useMemo(() => {
    if (!Array.isArray(posts)) return [];

    return posts
      .filter((post: any) => !post.isPinned
      //  && post.isArticle === true
      )
      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10);
  }, [posts]);

  const loginRedirect = () => {
    navigate(RouteConstants.LOGIN);
  };

  const responsive = [
    { minWidth: 1024.9, itemsToShow: 3 },
    { minWidth: 768, itemsToShow: 2 },
    { maxWidth: 767, itemsToShow: 1 },
  ];

  if (!Array.isArray(recentArticles) || recentArticles.length === 0) {
    console.log('No recent articles available to display in carousel');
    return null;
  }

  const renderArticleCard = (article: any, index: number) => {
    return (
      <Card className="PostCard">
        <div className="Header">
          <div className="Profile">
            <div className="owner">
              <div className="ownerPic">
                {article?.createdBy?.image ? (
                  <img src={article.createdBy.image} alt="" />
                ) : (
                  <div className="ownerAlpha">
                    <span>{article?.createdBy?.name?.charAt(0)}</span>
                  </div>
                )}
              </div>
              <div className="ownerName">{article?.createdBy?.name}</div>
            </div>
          </div>
        </div>

        <div className="Body">
          {article?.medias?.length > 0 && (
            <div className="GridWrapper">
              <div className="ImageGrid">
                <div className={cn('grid-container', {
                  'single-image': article?.medias.length === 1,
                  'two-image-grid': article?.medias.length === 2,
                  'three-image-grid': article?.medias.length === 3,
                  'four-image-grid': article?.medias.length === 4,
                })}>
                  {article?.medias?.map((media: any, mediaIndex: number) => {
                    if (mediaIndex < 4) {
                      return (
                        <div
                          key={mediaIndex}
                          className={cn(`eachThumbnail eachThumbnail-${mediaIndex + 1}`, {
                            'video-type': media.type === 'video',
                            'no-video-thumb': media.type === 'video' && !media.thumbnail,
                          })}
                          style={{
                            backgroundImage: `url(${media.type === 'video' && media.thumbnail ? media.thumbnail : media.url})`
                          }}
                        >
                          {media.type === 'video' && (
                            <div className="play-icon">
                              <i className="fa fa-play"></i>
                            </div>
                          )}
                          {article?.medias.length > 4 && mediaIndex === 3 && (
                            <div className="more-media-overlay">
                              <span>+{article.medias.length - 4}</span>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="article-content">
            <div className="article-title">
              {article?.title || 'Untitled Article'}
            </div>
            <div className="article-preview">
              {stripMarkdown(article?.description || '').substring(0, 150)}...
            </div>
            <div className="article-meta">
              <span className="article-date">
                <i className="fa fa-calendar"></i>
                {createdMonthDayYear(article?.createdAt)}
              </span>
              <Link 
                to={`/article/${article?._id}`} 
                className="read-more-btn"
                onClick={(e) => e.stopPropagation()}
              >
                Read Article
              </Link>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="recent-articles-carousel">
      <h4>Recent Articles</h4>
      <Carousel
        containerProps={{
          style: {
            width: '100%',
            justifyContent: 'space-between',
            userSelect: 'text',
          },
          className: 'carousel-wrapper',
        }}
        infinite={false}
        activeSlideIndex={activeSlide}
        onRequestChange={setActiveSlide}
        forwardBtnProps={{
          children: <i className="fa fa-arrow-right"></i>,
          className: 'control next',
        }}
        backwardBtnProps={{
          children: <i className="fa fa-arrow-left"></i>,
          className: 'control prev',
        }}
        dotsNav={{
          show: false,
        }}
        speed={100}
        responsiveProps={responsive}
        preventScrollOnSwipe={true}
      >
        {recentArticles.map((article: any, index: number) => (
          <div
            key={index}
            className="article-item"
            onClick={() => {
              navigate(`/article/${article._id}`);
            }}
          >
            {renderArticleCard(article, index)}
          </div>
        ))}
      </Carousel>
    </div>
  );
};

export default RecentArticlesCarousel;
